import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePublishTestimonialMutation, useToggleFeatureUseCaseMutation, useGetUseCaseByIdQuery, useUpdateReviewResponseMutation, useGetVendorMeQuery } from '../../store/vendorApi';
import ShareButtons from '../../components/public/ShareButtons';
import { PUBLIC_SITE_URL } from '../../utils/publicSite';
import {
  ArrowLeft, User, Building, Star, Calendar, Lightbulb,
  Award, TrendingUp, MessageSquare, FileText, Eye, Edit,
  Share2, Download, Copy, ExternalLink, CheckCircle, Clock,
  AlertTriangle, Send, Wand2, Edit3, Sparkles, X, Loader2
} from 'lucide-react';

const UseCaseSubmissionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get use case from location state
  const useCaseFromState = location.state?.testimonial || location.state?.useCase || null;

  // Fetch the use case using the new endpoint if we have promoterId, otherwise skip
  const { data: useCaseFromApi, isLoading } = useGetUseCaseByIdQuery(
    { promoterId: useCaseFromState?.promoterId || '', testimonialId: id || '' },
    { skip: !useCaseFromState?.promoterId || !id }
  );

  const displayUseCase = useCaseFromApi || useCaseFromState;

  const [showResponseSection, setShowResponseSection] = useState(false);
  const [responseMode, setResponseMode] = useState<'ai' | 'manual'>('ai');
  const [responseText, setResponseText] = useState(displayUseCase?.vendorResponse || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const productName = displayUseCase?.productId || 'N/A';

  const [toggleFeature, { isLoading: isTogglingFeature }] = useToggleFeatureUseCaseMutation();
  const [publishTestimonial, { isLoading: isPublishing }] = usePublishTestimonialMutation();
  const [updateUseCaseResponse, { isLoading: isSavingResponse }] = useUpdateReviewResponseMutation();
  const { data: vendor } = useGetVendorMeQuery();
  const vendorSlug = vendor?.pageInfo?.pageSlug || '';

  useEffect(() => {
    if (displayUseCase) {
      setResponseText(displayUseCase.vendorResponse || '');
    }
  }, [displayUseCase]);

  // Parse use case content
  const parsedContent = React.useMemo(() => {
    if (!displayUseCase?.content) return null;
    try {
      const parsed = JSON.parse(displayUseCase.content);
      return parsed;
    } catch {
      return { generated_use_case: displayUseCase.content };
    }
  }, [displayUseCase]);

  const generateAIResponse = async () => {
    if (!displayUseCase) return;
    setIsGenerating(true);
    
    // Simulate AI generation
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const aiResponse = `Thank you ${displayUseCase.promoterName} for this excellent use case! We're delighted that ${displayUseCase.productId || 'our product'} has delivered such impressive results for ${displayUseCase.promoterCompany}. Your detailed case study will be incredibly valuable for our community and will help other companies understand the real-world benefits of our solution. We look forward to continuing our partnership and achieving even greater success together!`;
    
    setResponseText(aiResponse);
    setIsGenerating(false);
  };

  const handleSaveResponse = async () => {
    if (!displayUseCase || !id) return;
    try {
      await updateUseCaseResponse({
        promoterId: displayUseCase.promoterId,
        testimonialId: id,
        request: { response: responseText }
      }).unwrap();
      setShowResponseSection(false);
    } catch (error) {
      console.error('Error saving response:', error);
      alert('Failed to save response. Please try again.');
    }
  };

  const handlePublish = async (isPublished: boolean) => {
    if (!displayUseCase || !id) return;
    try {
      await publishTestimonial({ promoterId: displayUseCase.promoterId, testimonialId: id, isPublished }).unwrap();
    } catch (error) {
      console.error('Error updating publish status:', error);
    }
  };

  const handleToggleFeature = async () => {
    if (!displayUseCase || !id) return;

    try {
      await toggleFeature({ promoterId: displayUseCase.promoterId, testimonialId: id }).unwrap();
    } catch (error) {
      console.error('Error toggling feature:', error);
    }
  };

  const handleViewPublicUseCase = () => {
    window.open(`/public-use-case/${id}`, '_blank');
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      COMPLETED: 'bg-gray-100 text-gray-800',
      PUBLISHED: 'bg-green-100 text-green-800',
      FEATURED: 'bg-purple-100 text-purple-800'
    };

    const labels = {
      COMPLETED: 'Completed',
      PUBLISHED: 'Published',
      FEATURED: 'Featured'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status as keyof typeof labels] || status}
      </span>
    );
  };

  const getQualityBadge = (quality: string) => {
    const badges = {
      high: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[quality as keyof typeof badges]}`}>
        {quality.charAt(0).toUpperCase() + quality.slice(1)} Quality
      </span>
    );
  };

  const renderContent = (content: string) => {
    if (!content) return <p className="text-gray-500">No content available</p>;
    
    return content.split('\n').map((line, index) => {
      if (line.startsWith('# ')) {
        return <h1 key={index} className="text-4xl font-bold text-gray-900 mb-8 border-b border-gray-200 pb-4">{line.substring(2)}</h1>;
      } else if (line.startsWith('## ')) {
        return <h2 key={index} className="text-2xl font-semibold text-gray-900 mt-10 mb-6 text-blue-600">{line.substring(3)}</h2>;
      } else if (line.startsWith('- ')) {
        return <li key={index} className="text-gray-700 ml-6 mb-2 leading-relaxed list-disc">{line.substring(2)}</li>;
      } else if (line.trim() === '') {
        return <div key={index} className="h-4" />;
      } else {
        return <p key={index} className="text-gray-700 mb-6 leading-relaxed text-lg">{line}</p>;
      }
    });
  };

  if (!displayUseCase) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#4339A0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading Use Case</h2>
          <button onClick={() => navigate('/use-cases')} className="text-[#4339A0] hover:text-[#3d2f8f] font-medium">
            Back to Use Cases
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/use-cases')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center space-x-4">
                <div className="text-3xl text-purple-600">
                  📄
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{displayUseCase.title || 'Use Case Submission'}</h1>
                  <div className="flex items-center space-x-4 mt-1">
                    {(displayUseCase.useCaseIndustry || displayUseCase.industry) && <span className="text-gray-600">{displayUseCase.useCaseIndustry || displayUseCase.industry}</span>}
                    {(displayUseCase.useCaseIndustry || displayUseCase.industry) && <span className="text-gray-400">•</span>}
                    {(displayUseCase.useCaseCompanySize || displayUseCase.companySize) && <span className="text-gray-600">{displayUseCase.useCaseCompanySize || displayUseCase.companySize}</span>}
                    {(displayUseCase.useCaseCompanySize || displayUseCase.companySize) && <span className="text-gray-400">•</span>}
                    {getStatusBadge(displayUseCase.status)}
                    {displayUseCase.quality && getQualityBadge(displayUseCase.quality)}
                  </div>
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleViewPublicUseCase}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>View Public Use Case</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Promoter Information */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Submitted by</h2>
              <div className="flex items-center space-x-6">
                <div className="w-20 h-20 bg-[#4339A0] rounded-full flex items-center justify-center text-white font-semibold text-2xl">
                  {(displayUseCase.promoterName || 'U').split(' ').map((n: string) => n[0]).join('') || 'U'}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900">{displayUseCase.promoterName || 'Unknown'}</h3>
                  <p className="text-lg text-gray-600 flex items-center mt-2">
                    <Building className="w-5 h-5 mr-2" />
                    {displayUseCase.promoterCompany || 'No company'}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-3">
                    <span className="text-gray-500 whitespace-nowrap">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Submitted: {new Date(displayUseCase.createdAt).toLocaleDateString('en-US')}
                    </span>
                    <span className="text-gray-500 flex items-center gap-1 min-w-0">
                      <span className="shrink-0">Product:</span>
                      <span className="font-medium text-gray-700 break-all">{productName}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Use case content */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Use Case Content</h2>
              <article className="prose prose-lg max-w-none">
                {parsedContent?.generated_use_case ? (
                  renderContent(parsedContent.generated_use_case)
                ) : (
                  <p className="text-gray-700 whitespace-pre-wrap">{displayUseCase.content || 'No content'}</p>
                )}
              </article>
            </div>

            {/* Questions and Answers */}
            {parsedContent?.questions_and_answers && parsedContent.questions_and_answers.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Questions & Answers</h2>
                <div className="space-y-4">
                  {parsedContent.questions_and_answers.map((qa: any, index: number) => (
                    <div key={index} className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">{qa.question}</h3>
                      <div className="flex flex-wrap gap-2">
                        {qa.selected_choices?.map((choice: string, i: number) => (
                          <span key={i} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                            {choice}
                          </span>
                        ))}
                        {qa.answer && <p className="text-gray-700 mt-2 w-full">{qa.answer}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Company Response */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Company Response</h2>
                <button
                  onClick={() => setShowResponseSection(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>{displayUseCase.vendorResponse ? 'Edit Response' : 'Add Response'}</span>
                </button>
              </div>
              
              {displayUseCase.vendorResponse && !showResponseSection ? (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded">
                  <div className="flex items-center space-x-2 mb-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <Building className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-medium text-blue-900 text-lg">Company Response</span>
                  </div>
                  <p className="text-blue-800 text-lg leading-relaxed">{displayUseCase.vendorResponse}</p>
                </div>
              ) : !showResponseSection ? (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No response yet</h3>
                  <p className="text-gray-600">Click "Add Response" to reply to this use case submission</p>
                </div>
              ) : null}

              {/* Response Section */}
              {showResponseSection && (
                <div className="mt-8 border-t border-gray-200 pt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">
                    {displayUseCase.vendorResponse ? 'Edit Response' : 'Add Response'}
                  </h3>
                  
                  {/* Response Mode Selection */}
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-4">Response Mode</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => setResponseMode('ai')}
                        className={`p-6 rounded-lg border-2 transition-all duration-200 ${
                          responseMode === 'ai'
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="text-center">
                          <Wand2 className="w-10 h-10 text-purple-600 mx-auto mb-3" />
                          <h5 className="font-medium text-gray-900">AI Generated</h5>
                          <p className="text-sm text-gray-600">Let AI create a professional response</p>
                        </div>
                      </button>

                      <button
                        onClick={() => setResponseMode('manual')}
                        className={`p-6 rounded-lg border-2 transition-all duration-200 ${
                          responseMode === 'manual'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="text-center">
                          <Edit3 className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                          <h5 className="font-medium text-gray-900">Manual</h5>
                          <p className="text-sm text-gray-600">Write your own response</p>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* AI Generation */}
                  {responseMode === 'ai' && (
                    <div className="mb-6">
                      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-200">
                        <div className="flex items-center space-x-3 mb-4">
                          <Sparkles className="w-6 h-6 text-purple-600" />
                          <h5 className="font-medium text-purple-900">AI Response Generator</h5>
                        </div>
                        <p className="text-sm text-purple-800 mb-4">
                          Our AI will generate a professional and personalized response based on the use case content.
                        </p>
                        <button
                          onClick={generateAIResponse}
                          disabled={isGenerating}
                          className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                        >
                          {isGenerating ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              <span>Generating...</span>
                            </>
                          ) : (
                            <>
                              <Wand2 className="w-5 h-5" />
                              <span>Generate Response</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Response Text Area */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Response Text
                    </label>
                    <textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      rows={8}
                      className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-lg"
                      placeholder={responseMode === 'ai' 
                        ? 'Click "Generate Response" to create an AI response...'
                        : 'Write your response to this use case...'
                      }
                    />
                  </div>

                  {/* Preview */}
                  {responseText && (
                    <div className="mb-6">
                      <h5 className="font-medium text-gray-900 mb-3">Preview</h5>
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded">
                        <div className="flex items-center space-x-2 mb-3">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                            <Building className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-medium text-blue-900 text-lg">Company Response Preview</span>
                        </div>
                        <p className="text-blue-800 text-lg leading-relaxed">{responseText}</p>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex space-x-4">
                    <button
                      onClick={() => setShowResponseSection(false)}
                      className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveResponse}
                      disabled={!responseText.trim() || isSavingResponse}
                      className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-medium"
                    >
                      {isSavingResponse ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          <span>Save Response</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                {/* Publish Toggle */}
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    {(displayUseCase.status === 'PUBLISHED' || displayUseCase.status === 'FEATURED')
                      ? <CheckCircle className="w-5 h-5 text-green-600" />
                      : <Clock className="w-5 h-5 text-gray-400" />
                    }
                    <span className={`text-sm font-medium ${(displayUseCase.status === 'PUBLISHED' || displayUseCase.status === 'FEATURED') ? 'text-green-700' : 'text-gray-600'}`}>
                      {(displayUseCase.status === 'PUBLISHED' || displayUseCase.status === 'FEATURED') ? 'Published' : 'Not Published'}
                    </span>
                  </div>
                  <button
                    role="switch"
                    aria-checked={displayUseCase.status === 'PUBLISHED' || displayUseCase.status === 'FEATURED'}
                    disabled={isPublishing}
                    onClick={() => handlePublish(displayUseCase.status === 'COMPLETED')}
                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                      (displayUseCase.status === 'PUBLISHED' || displayUseCase.status === 'FEATURED') ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-flex h-5 w-5 transform items-center justify-center rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 mt-1 ${
                        (displayUseCase.status === 'PUBLISHED' || displayUseCase.status === 'FEATURED') ? 'translate-x-6 ml-[-2px]' : 'translate-x-1'
                      }`}
                    >
                      {isPublishing && <Loader2 className="w-3 h-3 text-gray-400 animate-spin" />}
                    </span>
                  </button>
                </div>

                {/* Toggle Feature for published/featured */}
                {(displayUseCase.status === 'PUBLISHED' || displayUseCase.status === 'FEATURED') && (
                  <button
                    onClick={handleToggleFeature}
                    disabled={isTogglingFeature}
                    className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                      displayUseCase.isFeatured
                        ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-300 hover:bg-yellow-200'
                        : 'bg-gray-100 text-gray-700 border-2 border-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {isTogglingFeature ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <Star className="w-5 h-5" />
                        <span>{displayUseCase.isFeatured ? 'Remove from Featured' : 'Mark as Featured'}</span>
                      </>
                    )}
                  </button>
                )}

                {/* Add Response */}
                <button
                  onClick={() => setShowResponseSection(true)}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <MessageSquare className="w-5 h-5" />
                  <span>{displayUseCase.vendorResponse ? 'Edit Response' : 'Add Response'}</span>
                </button>

                {/* View Public Use Case */}
                <button
                  onClick={handleViewPublicUseCase}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <ExternalLink className="w-5 h-5" />
                  <span>View Public Use Case</span>
                </button>

                {(displayUseCase.status === 'PUBLISHED' || displayUseCase.status === 'FEATURED') && (
                  <div className="border-t border-gray-100 pt-3 mt-1">
                    <p className="text-xs text-gray-500 mb-2">Share this use case</p>
                    <ShareButtons
                      url={`${PUBLIC_SITE_URL}/use-case/${id}${vendorSlug ? `?slug=${vendorSlug}` : ''}`}
                      title={displayUseCase.title || 'Use Case'}
                      compact
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Submission Details */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Submission Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <span className="text-sm text-gray-500">Product</span>
                  <p className="font-medium text-gray-900 mt-0.5 break-words leading-snug">{productName}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Industry</span>
                  <p className="font-medium text-gray-900 mt-0.5">{displayUseCase.useCaseIndustry || displayUseCase.industry || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Company Size</span>
                  <p className="font-medium text-gray-900 mt-0.5">{displayUseCase.useCaseCompanySize || displayUseCase.companySize || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Submitted:</span>
                  <p className="font-medium text-gray-900">
                    {new Date(displayUseCase.createdAt).toLocaleDateString('en-US')}
                  </p>
                </div>
              </div>

              {displayUseCase.notes && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <MessageSquare className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-yellow-900">Internal Notes</h4>
                      <p className="text-yellow-800 text-sm mt-1">{displayUseCase.notes}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Status Information */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Information</h3>
              
              {displayUseCase.status === 'COMPLETED' && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <Clock className="w-6 h-6 text-gray-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-gray-900">Not Published</h4>
                      <p className="text-sm text-gray-800 mt-1">
                        This use case is not published yet. Publish it to show it on your public page.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {(displayUseCase.status === 'PUBLISHED' || displayUseCase.status === 'FEATURED') && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-green-900">Published</h4>
                      <p className="text-sm text-green-800 mt-1">
                        This use case is visible on your public page. {displayUseCase.status === 'FEATURED' ? 'It is featured.' : 'You can mark it as featured.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default UseCaseSubmissionDetail;